// /apps/patient/src/pages/records/EncounterDetail.tsx

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { patientApiService } from '../../services/api';
import { PageHeader } from '../../components/layout/PageHeader';
import { 
  Stethoscope, Calendar, Clock, User, MapPin, 
  FileText, Activity, Heart, Thermometer, 
  Weight, Ruler, AlertCircle, Printer,
  ChevronDown, ChevronRight
} from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '../../lib/utils';

interface EncounterDetail {
  id: string;
  created_at: number;
  encounter_date: string;
  encounter_type: string;
  visit_type?: string;
  chief_complaint?: string;
  reason_for_visit?: string;
  presenting_problems?: string[];
  status: string;
  provider_name?: string;
  location_name?: string;
  clinical_note_id?: string;
  documentation_status?: string;
  duration_minutes?: number;
  vitals?: any;
  billing_codes?: {
    cpt_codes?: string[];
    icd10_codes?: string[];
  };
}

interface TemplateSection {
  id: string;
  name: string;
  visibleToPatient?: boolean;
  legacySoapSection?: string | null;
  fields?: any[];
}

interface ClinicalNote {
  id: string;
  created_at: number;
  note_type: string;
  subjective?: any;
  objective?: any;
  assessment?: any;
  plan?: any;
  signed?: boolean;
  signed_at?: string;
  signed_by?: string;
  template?: { sections?: TemplateSection[] };
  sections_data?: {
    templateId?: string;
    sections?: { sectionId: string; sectionName: string; fields: any[] }[];
  };
}

export function EncounterDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [encounter, setEncounter] = useState<EncounterDetail | null>(null);
  const [clinicalNote, setClinicalNote] = useState<ClinicalNote | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    subjective: true,
    objective: true,
    assessment: true,
    plan: true
  });

  useEffect(() => {
    if (id) {
      fetchEncounterDetail();
    }
  }, [id]);

  const fetchEncounterDetail = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch encounter details
      const encounterData = await patientApiService.getEncounterDetail(id!);
      setEncounter(encounterData);
      
      // Fetch clinical notes by encounter_id
      try {
        const notesResult = await patientApiService.getClinicalNotes({ encounter_id: id });
        const encNotes = notesResult?.items || notesResult || [];
        if (encNotes.length > 0) {
          setClinicalNote(encNotes[0]);
        }
      } catch (noteErr) {
        console.error('Error fetching clinical notes:', noteErr);
      }
    } catch (err: any) {
      console.error('Error fetching encounter detail:', err);
      setError(err.message || 'Failed to load encounter details');
    } finally {
      setLoading(false);
    }
  };

  const formatEncounterType = (type: string) => {
    return type
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const formatPropertyName = (property: string): string => {
    // Skip properties with 'id' or 'ids' in them
    if (property.includes('id') || property.includes('ids')) {
      return '';
    }
    
    // Convert snake_case to Title Case
    return property
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const renderSOAPContent = (content: any, depth: number = 0): JSX.Element | null => {
    if (!content) return null;
    
    // If it's a simple string or number, render it directly
    if (typeof content === 'string' || typeof content === 'number') {
      return <p className="text-gray-700 whitespace-pre-wrap">{content}</p>;
    }
    
    // If it's an array, render each item
    if (Array.isArray(content)) {
      return (
        <ul className="list-disc list-inside space-y-1">
          {content.map((item, index) => (
            <li key={index} className="text-gray-700">
              {typeof item === 'object' ? renderSOAPContent(item, depth + 1) : item}
            </li>
          ))}
        </ul>
      );
    }
    
    // If it's an object, render its properties
    if (typeof content === 'object') {
      const validProperties = Object.entries(content).filter(([key]) => {
        const formattedName = formatPropertyName(key);
        return formattedName !== '';
      });
      
      if (validProperties.length === 0) return null;
      
      return (
        <div className={cn("space-y-3", depth > 0 && "ml-4")}>
          {validProperties.map(([key, value]) => {
            const formattedName = formatPropertyName(key);
            
            // Skip empty values
            if (!value || (Array.isArray(value) && value.length === 0)) {
              return null;
            }
            
            return (
              <div key={key}>
                <p className="text-sm font-medium text-gray-600 mb-1">{formattedName}:</p>
                <div className="ml-2">
                  {renderSOAPContent(value, depth + 1)}
                </div>
              </div>
            );
          })}
        </div>
      );
    }
    
    return null;
  };

  const renderVitals = (vitals: any) => {
    if (!vitals || Object.keys(vitals).length === 0) return null;
    
    // Normalize vitals in case it's an array
    const vitalsData = Array.isArray(vitals) ? vitals[0] || {} : vitals;
    
    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {vitalsData.blood_pressure_systolic && (
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="flex items-center gap-2 text-gray-600 mb-1">
              <Heart className="h-4 w-4" />
              <span className="text-sm">Blood Pressure</span>
            </div>
            <p className="text-lg font-semibold text-gray-900">
              {vitalsData.blood_pressure_systolic}/{vitalsData.blood_pressure_diastolic} mmHg
            </p>
          </div>
        )}
        
        {vitalsData.heart_rate && (
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="flex items-center gap-2 text-gray-600 mb-1">
              <Activity className="h-4 w-4" />
              <span className="text-sm">Heart Rate</span>
            </div>
            <p className="text-lg font-semibold text-gray-900">{vitalsData.heart_rate} bpm</p>
          </div>
        )}
        
        {vitalsData.temperature && (
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="flex items-center gap-2 text-gray-600 mb-1">
              <Thermometer className="h-4 w-4" />
              <span className="text-sm">Temperature</span>
            </div>
            <p className="text-lg font-semibold text-gray-900">{vitalsData.temperature}°F</p>
          </div>
        )}
        
        {vitalsData.weight && (
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="flex items-center gap-2 text-gray-600 mb-1">
              <Weight className="h-4 w-4" />
              <span className="text-sm">Weight</span>
            </div>
            <p className="text-lg font-semibold text-gray-900">{vitalsData.weight} lbs</p>
          </div>
        )}
        
        {vitalsData.height && (
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="flex items-center gap-2 text-gray-600 mb-1">
              <Ruler className="h-4 w-4" />
              <span className="text-sm">Height</span>
            </div>
            <p className="text-lg font-semibold text-gray-900">{vitalsData.height} in</p>
          </div>
        )}
        
        {vitalsData.respiratory_rate && (
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="flex items-center gap-2 text-gray-600 mb-1">
              <Activity className="h-4 w-4" />
              <span className="text-sm">Respiratory Rate</span>
            </div>
            <p className="text-lg font-semibold text-gray-900">{vitalsData.respiratory_rate} /min</p>
          </div>
        )}
        
        {vitalsData.oxygen_saturation && (
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="flex items-center gap-2 text-gray-600 mb-1">
              <Activity className="h-4 w-4" />
              <span className="text-sm">O2 Saturation</span>
            </div>
            <p className="text-lg font-semibold text-gray-900">{vitalsData.oxygen_saturation}%</p>
          </div>
        )}
        
        {vitalsData.pain_level !== undefined && vitalsData.pain_level !== null && (
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="flex items-center gap-2 text-gray-600 mb-1">
              <AlertCircle className="h-4 w-4" />
              <span className="text-sm">Pain Level</span>
            </div>
            <p className="text-lg font-semibold text-gray-900">{vitalsData.pain_level}/10</p>
          </div>
        )}
      </div>
    );
  };

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <PageHeader title="Visit Details" showBack />
        <div className="mt-8">
          <div className="animate-pulse space-y-4">
            <div className="h-48 bg-gray-200 rounded-lg"></div>
            <div className="h-32 bg-gray-200 rounded-lg"></div>
            <div className="h-64 bg-gray-200 rounded-lg"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !encounter) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <PageHeader title="Visit Details" showBack />
        <div className="mt-8 bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-3" />
          <p className="text-red-700">{error || 'Visit record not found'}</p>
          <button
            onClick={() => navigate('/records')}
            className="mt-4 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
          >
            Back to Health Records
          </button>
        </div>
      </div>
    );
  }

  // Build SOAP sections, filtering by visibleToPatient when template flags exist
  const allSoapSections = [
    { key: 'subjective', title: 'Subjective', icon: User },
    { key: 'objective', title: 'Objective', icon: Stethoscope },
    { key: 'assessment', title: 'Assessment', icon: FileText },
    { key: 'plan', title: 'Plan', icon: Activity }
  ];

  const templateSections = clinicalNote?.template?.sections;
  const hasVisibilityFlags = templateSections?.some(
    (s: TemplateSection) => s.visibleToPatient !== undefined
  );

  // If the template has visibleToPatient flags, filter SOAP sections accordingly.
  // Match template sections to SOAP sections via legacySoapSection mapping.
  // Legacy templates without flags: show all sections (backwards compatible).
  const soapSections = hasVisibilityFlags
    ? allSoapSections.filter(({ key }) => {
        const matchingSection = templateSections?.find(
          (s: TemplateSection) => s.legacySoapSection === key
        );
        // Show if any template section mapped to this SOAP key is visible to patient
        return matchingSection ? matchingSection.visibleToPatient === true : false;
      })
    : allSoapSections;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <PageHeader 
        title="Visit Details" 
        showBack
        actions={
          <button
            onClick={handlePrint}
            className="flex items-center gap-2 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <Printer className="h-4 w-4" />
            Print
          </button>
        }
      />

      {/* Visit Header */}
      <div className="mt-6 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-primary/10 rounded-lg">
            <Stethoscope className="h-8 w-8 text-primary" />
          </div>
          <div className="flex-1">
            <h2 className="text-2xl font-bold text-gray-900">
              {formatEncounterType(encounter.encounter_type)}
            </h2>
            <div className="flex flex-wrap items-center gap-4 mt-3 text-sm text-gray-600">
              <span className="flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                {format(new Date(encounter.encounter_date), 'EEEE, MMMM d, yyyy')}
              </span>
              <span className="flex items-center gap-1">
                <Clock className="h-4 w-4" />
                {format(new Date(encounter.encounter_date), 'h:mm a')}
              </span>
              {encounter.provider_name && (
                <span className="flex items-center gap-1">
                  <User className="h-4 w-4" />
                  {encounter.provider_name}
                </span>
              )}
              {encounter.location_name && (
                <span className="flex items-center gap-1">
                  <MapPin className="h-4 w-4" />
                  {encounter.location_name}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Chief Complaint / Reason for Visit */}
        {(encounter.chief_complaint || encounter.reason_for_visit) && (
          <div className="mt-6 pt-6 border-t">
            <h3 className="text-sm font-medium text-gray-500 mb-2">Reason for Visit</h3>
            <p className="text-gray-900">
              {encounter.chief_complaint || encounter.reason_for_visit}
            </p>
          </div>
        )}

        {/* Presenting Problems */}
        {encounter.presenting_problems && encounter.presenting_problems.length > 0 && (
          <div className="mt-4">
            <h3 className="text-sm font-medium text-gray-500 mb-2">Presenting Problems</h3>
            <ul className="list-disc list-inside space-y-1">
              {encounter.presenting_problems.map((problem, index) => (
                <li key={index} className="text-gray-900">{problem}</li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Vitals */}
      {encounter.vitals && Object.keys(encounter.vitals).length > 0 && (
        <div className="mt-6 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Vital Signs</h3>
          {renderVitals(encounter.vitals)}
        </div>
      )}

      {/* Clinical Note (SOAP) */}
      {clinicalNote && (
        <div className="mt-6 space-y-4">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Clinical Note</h3>
            {clinicalNote.signed && (
              <p className="text-sm text-gray-600 mb-4">
                Signed on {format(new Date(clinicalNote.signed_at!), 'MMM d, yyyy at h:mm a')}
              </p>
            )}
          </div>

          {/* SOAP Sections */}
          {soapSections.map(({ key, title, icon: Icon }) => {
            const content = clinicalNote[key as keyof ClinicalNote];
            if (!content || (typeof content === 'object' && Object.keys(content).length === 0)) {
              return null;
            }

            const isExpanded = expandedSections[key];

            return (
              <div key={key} className="bg-white rounded-lg shadow-sm border border-gray-200">
                <button
                  onClick={() => toggleSection(key)}
                  className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <Icon className="h-5 w-5 text-primary" />
                    <h4 className="text-lg font-semibold text-gray-900">{title}</h4>
                  </div>
                  {isExpanded ? (
                    <ChevronDown className="h-5 w-5 text-gray-400" />
                  ) : (
                    <ChevronRight className="h-5 w-5 text-gray-400" />
                  )}
                </button>
                
                {isExpanded && (
                  <div className="px-6 pb-6">
                    <div className="border-t pt-4">
                      {renderSOAPContent(content)}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Diagnoses */}
      {clinicalNote?.assessment?.diagnoses && clinicalNote.assessment.diagnoses.length > 0 && (
        <div className="mt-6 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Diagnoses</h3>
          <div className="space-y-2">
            {clinicalNote.assessment.diagnoses.map((diagnosis: any, index: number) => (
              <div key={index} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                <span className="text-sm font-medium text-gray-600 min-w-[80px]">
                  {diagnosis.code}
                </span>
                <span className="text-gray-900">
                  {diagnosis.description}
                  {diagnosis.clinical_status && (
                    <span className="ml-2 text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-700">
                      {diagnosis.clinical_status}
                    </span>
                  )}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Treatment Plan */}
      {clinicalNote?.plan?.treatment_plan && (
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-blue-900 mb-3">Your Treatment Plan</h3>
          <div className="text-blue-800 whitespace-pre-wrap">
            {clinicalNote.plan.treatment_plan}
          </div>
        </div>
      )}

      {/* Follow-up Information */}
      {clinicalNote?.plan?.follow_up && (
        <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-yellow-900 mb-3">Follow-up Instructions</h3>
          <div className="space-y-2 text-yellow-800">
            {clinicalNote.plan.follow_up.timeframe && (
              <p><strong>When:</strong> {clinicalNote.plan.follow_up.timeframe}</p>
            )}
            {clinicalNote.plan.follow_up.reason && (
              <p><strong>Reason:</strong> {clinicalNote.plan.follow_up.reason}</p>
            )}
            {clinicalNote.plan.follow_up.specific_instructions && (
              <p><strong>Instructions:</strong> {clinicalNote.plan.follow_up.specific_instructions}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}